import React, { useMemo } from 'react'

import { CalendarHeader } from 'components/calendarHeader'
import { CalendarMonth } from 'components/calendarMonth'
import { CalendarWeek } from 'components/calendarWeek'
import { useAgenda } from 'context'
import {
	addDays,
	addMonths,
	eachDayOfInterval,
	endOfMonth,
	format,
	isWithinInterval,
	parseISO,
	startOfMonth,
	startOfWeek,
	subMonths
} from 'date-fns'
import {
	CalendarDay,
	CalendarEvent,
	formatLocalTime,
	ViewTypeCalendar
} from 'utils'

interface CalendarProps {
	events: Array<CalendarEvent>
}

export const Calendar: React.FC<CalendarProps> = (props): JSX.Element => {
	const { events } = props

	const {
		currentMonth,
		setCurrentMonth,
		currentWeek,
		setCurrentWeek,
		view,
		setView
	} = useAgenda()
	const firstDayOfMonth = startOfMonth(currentMonth)
	const lastDayOfMonth = endOfMonth(currentMonth)
	const firstCalendarDay = useMemo(
		() => startOfWeek(firstDayOfMonth, { weekStartsOn: 1 }),
		[currentMonth]
	)
	const firstCalendarDayWeek = useMemo(
		() => startOfWeek(currentWeek, { weekStartsOn: 1 }),
		[currentWeek]
	)

	const days: CalendarDay[] = useMemo(() => {
		const intervalEnd =
			view === 'week' ? addDays(firstCalendarDay, 6) : lastDayOfMonth
		return eachDayOfInterval({
			start: firstCalendarDay,
			end: intervalEnd
		}).map(date => {
			const formattedDate = format(date, 'yyyy-MM-dd')
			return {
				date: formattedDate,
				isCurrentMonth: date >= firstDayOfMonth && date <= lastDayOfMonth,
				events:
					events.filter(event => {
						const localEventDate = formatLocalTime(
							event.publish_date,
							'YYYY-MM-DD'
						)
						return formattedDate === localEventDate
					}) || [],
				isSelected: false,
				isToday: formattedDate === format(new Date(), 'yyyy-MM-dd')
			}
		})
	}, [firstCalendarDay, lastDayOfMonth, events, view])

	const nextMonth = (): void => setCurrentMonth(addMonths(currentMonth, 1))
	const prevMonth = (): void => setCurrentMonth(subMonths(currentMonth, 1))
	const changeView = (typeView: ViewTypeCalendar): void => setView(typeView)

	const nextPeriod = (): void => {
		setCurrentWeek(addDays(currentWeek, 7))
	}

	const prevPeriod = (): void => {
		setCurrentWeek(addDays(currentWeek, -7))
	}

	const currentWeekEvents = useMemo(() => {
		const end = addDays(firstCalendarDayWeek, 7)

		return events.filter(event =>
			isWithinInterval(parseISO(event.publish_date), {
				start: firstCalendarDayWeek,
				end
			})
		)
	}, [days, currentMonth])

	return (
		<div className="flex h-full flex-col">
			<CalendarHeader
				view={view}
				prevMonth={prevMonth}
				nextMonth={nextMonth}
				changeView={changeView}
				currentMonth={currentMonth}
				firstCalendarDay={
					view === 'week' ? firstCalendarDayWeek : firstCalendarDay
				}
				nextPeriod={nextPeriod}
				prevPeriod={prevPeriod}
			/>
			{view === 'month' ? (
				<CalendarMonth days={days} currentMonth={currentMonth} />
			) : (
				<CalendarWeek currentMonth={currentWeek} days={currentWeekEvents} />
			)}
		</div>
	)
}
